<script setup lang="ts">
import SeoHead from '@/components/marketing/SeoHead.vue';
import MarketingLayout from '@/layouts/MarketingLayout.vue';

const lastUpdated = 'August 2, 2026';

const sections = [
    {
        title: '1. Who we are',
        paragraphs: [
            'CreativeTint ("we", "us") is a content marketing platform that helps teams create, schedule, and publish social media content, and analyze how it performs. This policy explains what data we collect, why we collect it, and the choices you have.',
            'If anything here is unclear, email us at privacy@creativetint.com and a human will answer.',
        ],
    },
    {
        title: '2. Data we collect',
        paragraphs: ['We collect only what the product needs to work:'],
        items: [
            'Account data — your name, email address, and profile details, provided when you create an account.',
            'Brand profile — the niche, audience, and tone-of-voice information you enter during onboarding, used to personalize generated content.',
            'Content you create — prompts, source material you upload or link, generated drafts, and scheduled posts.',
            'Connected accounts — when you connect a social platform (LinkedIn, X, Instagram, Facebook, TikTok, YouTube, Threads), we store the access tokens the platform issues and basic account metadata (name, handle, avatar). We never see or store your platform passwords.',
            'Engagement data — likes, comments, shares, views, and impressions for posts you published through CreativeTint, retrieved from each platform\u2019s official API.',
            'Billing data — handled by Creem (Merchant of Record). We store your plan, subscription status, and credit balance; we never store card numbers.',
            'Usage data — standard technical logs (IP address, browser, pages visited) used for security and to improve the product.',
        ],
    },
    {
        title: '3. How we use your data',
        items: [
            'To provide the service — generating content, publishing to your connected platforms, and syncing engagement back into your analytics.',
            'To personalize AI output using your brand profile and, where you opt in, your past content and performance data.',
            'To bill you for your subscription and manage credits.',
            'To secure the platform, prevent abuse, and comply with legal obligations.',
            'We do not sell your personal data. Ever.',
        ],
    },
    {
        title: '4. AI processing',
        paragraphs: [
            'When you generate content, your prompts, source material, and brand profile are sent to our AI providers (such as OpenAI, Replicate, and Deepgram) to produce the output you requested. We use providers under agreements that prohibit them from using your content to train their publicly available models.',
            'Generated drafts belong to you. We do not use your content to train models shared with other customers.',
        ],
    },
    {
        title: '5. Social platform data',
        paragraphs: [
            'Data we receive from social platform APIs (post metrics, comments, account metadata) is used solely to display and analyze your own posts inside your workspace, and to power features you explicitly enable — such as Autopilot follow-up drafts.',
            'We follow each platform\u2019s developer policies, including Meta Platform Terms, Google API Services User Data Policy, and TikTok Developer Terms. You can disconnect any platform at any time from the Connections page, which deletes the stored token. You can also revoke access from the platform\u2019s own settings.',
        ],
    },
    {
        title: '6. Third-party services',
        paragraphs: ['We rely on a small set of processors to run CreativeTint:'],
        items: [
            'Creem — payments and subscription billing (Merchant of Record).',
            'AI providers (OpenAI, Replicate, Deepgram) — content generation and transcription.',
            'Social platform APIs — publishing and engagement sync for the accounts you connect.',
            'Cloud hosting and storage providers — running the application and storing your content.',
        ],
    },
    {
        title: '7. Data retention & deletion',
        paragraphs: [
            'We keep your data for as long as your account is active. If you delete content, connected accounts, or your team, the associated data is removed from our systems. Backups are rotated on a fixed schedule, after which deleted data is gone from those too.',
            'To delete your entire account and all associated data, email privacy@creativetint.com from your account email and we will complete the deletion within 30 days.',
        ],
    },
    {
        title: '8. Security',
        paragraphs: [
            'All traffic is encrypted in transit (TLS). Social platform tokens are encrypted at rest. Access to production systems is restricted and audited. No system is perfectly secure, but if we ever become aware of a breach affecting your data, we will notify you promptly.',
        ],
    },
    {
        title: '9. Your rights',
        paragraphs: [
            'Depending on where you live (including under GDPR and CCPA), you have the right to access, correct, export, or delete your personal data, and to object to certain processing. Contact privacy@creativetint.com to exercise any of these rights — we respond to all requests within 30 days.',
        ],
    },
    {
        title: '10. Cookies',
        paragraphs: [
            'We use essential cookies for authentication and session security, and a small number of preference cookies (like remembering your sidebar state and theme). We do not use third-party advertising trackers.',
        ],
    },
    {
        title: '11. Changes to this policy',
        paragraphs: [
            'If we make material changes, we will notify you by email or an in-app notice before they take effect. The "last updated" date at the top always reflects the current version.',
        ],
    },
    {
        title: '12. Contact',
        paragraphs: [
            'Questions or requests: privacy@creativetint.com. We read everything.',
        ],
    },
];
</script>

<template>
    <SeoHead
        title="Privacy Policy — CreativeTint"
        description="How CreativeTint collects, uses, and protects your data — including connected social accounts, AI processing, retention, and your rights."
        path="/privacy"
        type="article"
    />
    <MarketingLayout>
        <div class="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-20">
            <p class="text-xs font-bold tracking-[0.2em] text-violet-500 dark:text-violet-300 uppercase">Legal</p>
            <h1 class="font-display mt-3 text-4xl font-extrabold tracking-tight md:text-5xl">Privacy Policy</h1>
            <p class="mt-3 text-sm text-muted-foreground">Last updated: {{ lastUpdated }}</p>

            <p class="mt-8 text-lg leading-relaxed text-muted-foreground">
                The short version: we collect only what the product needs, we never
                sell your data, your content is yours, and you can disconnect or
                delete everything at any time.
            </p>

            <div class="mt-12 space-y-10">
                <section v-for="s in sections" :key="s.title">
                    <h2 class="font-display text-xl font-bold tracking-tight">{{ s.title }}</h2>
                    <p
                        v-for="(p, i) in s.paragraphs ?? []"
                        :key="i"
                        class="mt-3 text-[15px] leading-relaxed text-muted-foreground"
                    >
                        {{ p }}
                    </p>
                    <ul v-if="s.items" class="mt-3 space-y-2.5">
                        <li
                            v-for="(item, i) in s.items"
                            :key="i"
                            class="flex items-start gap-2.5 text-[15px] leading-relaxed text-muted-foreground"
                        >
                            <span class="mt-2 size-1.5 flex-none rounded-full bg-violet-400" />
                            <span>{{ item }}</span>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </MarketingLayout>
</template>
